import React, { useContext } from 'react';
import { List, Tag, Empty, Typography, Tooltip, Popconfirm, Button, Space, Flex } from 'antd';
import { DeleteOutlined, WarningOutlined, FileTextOutlined, TagsOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import { ITicket } from '../../../interfaces';
import { formatDateStringToDateAndTime } from '../../../utils/Conversores';
import useAuth from '../../../hooks/useAuth';
import { ThemeContext } from '../../../contexts/ThemeContext';

interface TicketsHistoryCardProps {
  tickets?: ITicket[];
  onDeleteTicket?: (ticketId: number) => void;
  currentInvoiceId?: number;
}

export const TicketsHistoryCard: React.FC<TicketsHistoryCardProps> = ({
  tickets,
  onDeleteTicket,
  currentInvoiceId,
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const { user } = useAuth();
  const isAdmin = user?.role?.id === 1;

  if (!tickets) return null;

  const sorted = [...tickets].sort((a, b) => b.id - a.id);

  const orphanCount = sorted.filter(
    (t) => t.generated_by_invoice_id == null
  ).length;

  const cardBg = isDark ? '#141414' : '#ffffff';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(82, 196, 26, 0.15)' : 'rgba(82, 196, 26, 0.1)',
              color: '#52c41a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <TagsOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Bilhetes do Usuário no Sorteio
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Todos os bilhetes concorrentes emitidos para o usuário neste sorteio
            </div>
          </div>
        </div>

        <Space size={8}>
          <Tag color="processing" style={{ margin: 0, fontWeight: 600 }}>
            {sorted.length} bilhete(s)
          </Tag>

          {orphanCount > 0 && (
            <Tag color="error" style={{ margin: 0, fontWeight: 600 }}>
              {orphanCount} órfão(s)
            </Tag>
          )}
        </Space>
      </div>

      {/* Content */}
      <div style={{ padding: '20px' }}>
        {sorted.length === 0 ? (
          <Empty description="Nenhum bilhete encontrado para este usuário no sorteio" />
        ) : (
          <List
            dataSource={sorted}
            pagination={{
              pageSize: 24,
              hideOnSinglePage: true,
              showSizeChanger: false,
              size: 'small',
            }}
            grid={{
              gutter: 10,
              xs: 1,
              sm: 2,
              md: 3,
              lg: 4,
              xl: 6,
              xxl: 6,
            }}
            renderItem={(ticket) => {
              const isCurrent = ticket.generated_by_invoice_id === currentInvoiceId;
              const isOrphan = ticket.generated_by_invoice_id == null;

              let itemBg = isDark ? '#1f1f1f' : '#f8fafc';
              let itemBorder = isDark ? '#2a2a2a' : '#eaedf1';

              if (isCurrent) {
                itemBg = isDark ? 'rgba(82, 196, 26, 0.12)' : '#f6ffed';
                itemBorder = isDark ? 'rgba(82, 196, 26, 0.35)' : '#b7eb8f';
              } else if (isOrphan) {
                itemBg = isDark ? 'rgba(255, 77, 79, 0.12)' : '#fff2f0';
                itemBorder = isDark ? 'rgba(255, 77, 79, 0.35)' : '#ffccc7';
              }

              return (
                <List.Item key={ticket.id} style={{ marginBottom: 10 }}>
                  <div
                    style={{
                      background: itemBg,
                      border: `1px solid ${itemBorder}`,
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      transition: 'all .2s ease',
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      <Typography.Text
                        strong
                        style={{
                          fontSize: 15,
                          color: isCurrent ? '#52c41a' : textColor,
                        }}
                      >
                        #{ticket.id}
                      </Typography.Text>

                      {isAdmin && (
                        <Popconfirm
                          title="Excluir bilhete?"
                          description={`Deseja realmente excluir o bilhete #${ticket.id}?`}
                          okText="Excluir"
                          cancelText="Cancelar"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => onDeleteTicket?.(ticket.id)}
                        >
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            style={{ padding: 0, width: 22, height: 22 }}
                          />
                        </Popconfirm>
                      )}
                    </Flex>

                    <div>
                      {ticket.generated_by_invoice_id ? (
                        <Button
                          type="link"
                          size="small"
                          disabled={isCurrent}
                          icon={<FileTextOutlined />}
                          style={{
                            padding: 0,
                            height: 'auto',
                            fontSize: 12,
                            fontWeight: isCurrent ? 700 : 500,
                            color: isCurrent ? '#52c41a' : '#1677ff',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/nota/detalhes/${ticket.generated_by_invoice_id}`);
                          }}
                        >
                          {isCurrent ? 'Nota atual' : `Nota: #${ticket.generated_by_invoice_id}`}
                        </Button>
                      ) : (
                        <Tooltip title="Bilhete sem nota fiscal associada">
                          <Tag
                            color="error"
                            icon={<WarningOutlined />}
                            style={{ margin: 0, fontSize: 11 }}
                          >
                            Órfão
                          </Tag>
                        </Tooltip>
                      )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: labelColor }}>
                      {/* <span>Emitido:</span> */}
                      <span style={{ fontWeight: 600 }}>
                        {formatDateStringToDateAndTime(ticket.created_at)}
                      </span>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TicketsHistoryCard;